import gql from "graphql-tag";
import {
  useEffectOnce,
  useVisibleGroups,
} from "utils/customHooks/genericHooks";
import { useGeAndLtFromUrl } from "../components/Reports/reportUtils";
import { FLIGHT_LINK_TO_MACHEMETRY_ID_MAPPING } from "../constants/customers";
import { FLIGHT_PROD_URL } from "../constants/constants";
import { flightDashboardLinksAtom } from "../store/store";
import { useAtom } from "jotai";
import { ReportParams } from "reports/types";
import { useParams } from "react-router";
import { Process } from "types/types";

export const ROLLUP_QUERY = gql`
  query Rollup($rollupGroup: String!, $ge: String!) {
    rollup(rollupGroup: $rollupGroup, ge: $ge)
      @rest(type: "Rollup", path: "tiles/{args.rollupGroup}?ge={args.ge}") {
      rollupName
      perfTarget
      details {
        summaryGroup
        shortName
        showOutput
      }
    }
  }
`;

export const SUMMARY_QUERY = gql`
  query Summary($rollupGroup: String!, $summaryGroup: String!, $ge: String!) {
    summary(rollupGroup: $rollupGroup, summaryGroup: $summaryGroup, ge: $ge)
      @rest(
        type: "Summary"
        path: "tiles/{args.rollupGroup}/{args.summaryGroup}?ge={args.ge}"
      ) {
      longName
      shortName
      perfTarget
      outputLabel
      isIdleUntagged
      callStart
      details {
        recentTicks
        recentTicksGe
        recentTicksWindow
        ticksThisShift
        ticksThisShiftThisSku
        processId
        name
        status
        statusStart
        outputLabel
        showCountNotMetricOnTile
        callStart
        messageTime
        callTarget
        callTargetIcon
        skuCode
        skuName
        skuDescription
        skuStart
        operatorName
        operatorAvatar
        outputThisShift
        outputThisShiftThisSku
        isSelected
        teamSize
        opSku
      }
    }
  }
`;

export const useProcessGroupSummaryDetails = (flightGroup?: string) => {
  const processGroups = useVisibleGroups();

  const flightGroupData = processGroups?.find(
    group =>
      group.summaryGroup.toLowerCase() === flightGroup?.toLowerCase() ||
      group.shortName.toLowerCase() === flightGroup?.toLowerCase()
  );
  const summaryGroup = flightGroupData?.summaryGroup;
  const summaryDetails: Process[] = flightGroupData?.processes ?? [];

  return { summaryGroup, summaryDetails };
};

export const useFlightDashboardLinksByMachemetryId = (
  machemetryIds: string[],
  flightGroup?: string
) => {
  const { ckey: tenant } = useParams() as ReportParams;
  const { ge, lt } = useGeAndLtFromUrl();
  const { summaryGroup, summaryDetails } =
    useProcessGroupSummaryDetails(flightGroup);
  const [, setFlightDashboardLinks] = useAtom(flightDashboardLinksAtom);

  // If processId exists in the summaryDetails of the provided flight group
  // then that is how many links we expect to load
  const numberOfLinksExpected = summaryDetails.filter(process =>
    machemetryIds.includes(process.id)
  ).length;

  const hasHardcodedLinks = Boolean(
    FLIGHT_LINK_TO_MACHEMETRY_ID_MAPPING?.[tenant]
  );

  const links = machemetryIds
    .map(machemetryId => {
      const flightLinkToMachemetryIdMappingHarcoded =
        FLIGHT_LINK_TO_MACHEMETRY_ID_MAPPING[tenant]?.[machemetryId] || null;

      if (flightLinkToMachemetryIdMappingHarcoded) {
        const hardcodedDashboardLink = `${FLIGHT_PROD_URL}/${tenant}/dashboard/${flightLinkToMachemetryIdMappingHarcoded}?ge=${ge}&lt=${lt}`;

        return {
          machemetryId,
          dashboardLink: hardcodedDashboardLink,
        };
      } else {
        const flightDashboardLinksIdx = summaryDetails.findIndex(
          process => process.id === machemetryId
        );

        const validFlightDashboardLink =
          flightDashboardLinksIdx > -1 ? flightDashboardLinksIdx : null;

        const flightGroupString =
          summaryGroup === "" ? summaryGroup : `/${summaryGroup}`;
        const dashboardLink =
          flightGroup !== null && validFlightDashboardLink !== null
            ? `${FLIGHT_PROD_URL}/${tenant}/dashboard${flightGroupString}/${validFlightDashboardLink}?ge=${ge}&lt=${lt}`
            : null;

        return {
          machemetryId,
          dashboardLink,
        };
      }
    })
    // Get only those with valid links
    .filter(({ dashboardLink }) => Boolean(dashboardLink));

  const areHardcodedLinksReady = links.length === machemetryIds.length;

  // Ready to run when we have all links possible from summaryDetails or harcoded links exist for tenant
  const shouldRun =
    (numberOfLinksExpected > 0 && links.length >= numberOfLinksExpected) ||
    (hasHardcodedLinks && areHardcodedLinksReady);

  // store the links once all links available are populated
  useEffectOnce({
    callback: () => {
      setFlightDashboardLinks(links);
    },
    shouldRun,
    dependencies: [links],
  });

  // Return whether we should wait for the links to load or not based on valid summaryGroup and summaryDetails
  // This will prevent us from getting stuck in loading if the provided flightGroup does not exist in machemetry
  return Boolean(summaryGroup && summaryDetails.length > 0);
};
